
import { AIEvent, Comment, AppConfig, ReviewItem } from "../types";
import { DEFAULT_KEYWORDS } from "../constants";

const STORAGE_KEYS = { EVENTS: 'cp_events', COMMENTS: 'cp_comments', CONFIG: 'cp_config', REVIEW: 'cp_review_queue' };

const safeParse = <T>(key: string, defaultValue: T): T => {
  try {
    const data = localStorage.getItem(key);
    if (!data) return defaultValue;
    return JSON.parse(data) as T;
  } catch (e) {
    console.warn(`Failed to parse storage key: ${key}`, e);
    return defaultValue;
  }
};

export const storageService = {
  getEvents(): AIEvent[] {
    const events = safeParse<AIEvent[]>(STORAGE_KEYS.EVENTS, []);
    return events.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  },
  saveEvents(events: AIEvent[]) { localStorage.setItem(STORAGE_KEYS.EVENTS, JSON.stringify(events)); },
  addEvent(event: AIEvent) { const events = this.getEvents(); events.push(event); this.saveEvents(events); },
  updateEvent(updatedEvent: AIEvent) { const events = this.getEvents(); const index = events.findIndex(e => e.id === updatedEvent.id); if (index !== -1) { events[index] = updatedEvent; this.saveEvents(events); } },
  deleteEvent(eventId: string) { 
    const events = this.getEvents().filter(e => e.id !== eventId);
    this.saveEvents(events); 
  },
  getComments(eventId: string): Comment[] {
    const allComments = safeParse<Comment[]>(STORAGE_KEYS.COMMENTS, []);
    return allComments.filter(c => c.eventId === eventId);
  },
  addComment(comment: Comment) {
    const allComments = safeParse<Comment[]>(STORAGE_KEYS.COMMENTS, []);
    allComments.push(comment);
    localStorage.setItem(STORAGE_KEYS.COMMENTS, JSON.stringify(allComments));
  },
  getConfig(): AppConfig {
    let config = safeParse<AppConfig | null>(STORAGE_KEYS.CONFIG, null);
    
    // Prioritize Environment Variables from Vercel/Vite
    const envUrl = process.env.SUPABASE_URL;
    const envKey = process.env.SUPABASE_ANON_KEY;

    if (!config) {
      const defaultConfig: AppConfig = { 
        sheetId: '', 
        tabName: 'Sheet1', 
        googleClientId: '', 
        discoveryKeywords: DEFAULT_KEYWORDS, 
        supabaseTableName: 'AI Events_OneSource Cloud', // Correct default matching your DB
        supabaseCommentsTableName: 'event_comments',
        supabaseUrl: envUrl || '', 
        supabaseAnonKey: envKey || '',
        theme: 'dark'
      };
      this.saveConfig(defaultConfig); 
      return defaultConfig;
    }

    // Force override config with Env Vars if they exist (Deployment logic)
    if (envUrl) config.supabaseUrl = envUrl;
    if (envKey) config.supabaseAnonKey = envKey;

    if (!config.supabaseCommentsTableName) {
      config.supabaseCommentsTableName = 'event_comments';
    }
    
    return config;
  },
  saveConfig(config: AppConfig) { localStorage.setItem(STORAGE_KEYS.CONFIG, JSON.stringify(config)); },
  getReviewQueue(): ReviewItem[] { return safeParse<ReviewItem[]>(STORAGE_KEYS.REVIEW, []); },
  saveReviewQueue(queue: ReviewItem[]) { localStorage.setItem(STORAGE_KEYS.REVIEW, JSON.stringify(queue)); }
};
